<template>
  <div ref="containerRef" :class="[bem.b()]">
    <div :class="[bem.e('container')]">
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { createNamespace } from '@ll-plus/utils'
import { nextTick, onMounted, onUnmounted, ref, watch, type Ref } from 'vue'
import { watermarkProps } from './watermark'

defineOptions({ name: 'LlWatermark' })

const bem = createNamespace('watermark')

const props = defineProps(watermarkProps)

const containerRef: Ref<HTMLElement | null> = ref(null)
const watermarkRef: Ref<HTMLDivElement | null> = ref(null)
const observer: Ref<MutationObserver | null> = ref(null)
const resizeObserver: Ref<ResizeObserver | null> = ref(null)
// 创建清理函数数组
const cleanupFunctions: (() => void)[] = []
// 防抖定时器
let updateTimer: ReturnType<typeof setTimeout> | null = null
// 是否正在更新
let isUpdating = false

// 位置映射表
const positionMap = {
  topLeft: '10% 0%',
  top: '50% 0%',
  topRight: '90% 0%',
  leftTop: '0% 10%',
  left: '0% 50%',
  leftBottom: '0% 90%',
  rightTop: '100% 10%',
  right: '100% 50%',
  rightBottom: '100% 90%',
  bottomLeft: '10% 100%',
  bottom: '50% 100%',
  bottomRight: '90% 100%',
  center: '50% 50%',
}

// 计算水印单元尺寸
const calculateWatermarkSize = (ctx: CanvasRenderingContext2D) => {
  const defaultWidth = 120
  const defaultHeight = 64

  let width = props.width
  let height = props.height
  if (width > 0) {
    if (height <= 0 && props.content) {
      const contents = Array.isArray(props.content)
        ? props.content
        : [props.content]
      const lineHeight = props.fontSize * 1.5
      height = contents.length * lineHeight + props.fontSize
    } else if (height <= 0) {
      height = defaultHeight
    }
  } else if (height > 0) {
    if (width <= 0 && props.content) {
      const contents = Array.isArray(props.content)
        ? props.content
        : [props.content]
      let maxTextWidth = 0

      ctx.font = `${props.fontWeight} ${props.fontSize}px ${props.fontFamily}`

      contents.forEach((text) => {
        const textWidth = ctx.measureText(text).width
        if (textWidth > maxTextWidth) maxTextWidth = textWidth
      })

      width = maxTextWidth
    } else if (width <= 0) {
      width = defaultWidth
    }
  } else {
    if (props.content) {
      const contents = Array.isArray(props.content)
        ? props.content
        : [props.content]
      let maxTextWidth = 0

      ctx.font = `${props.fontWeight} ${props.fontSize}px ${props.fontFamily}`

      contents.forEach((text) => {
        const textWidth = ctx.measureText(text).width
        if (textWidth > maxTextWidth) maxTextWidth = textWidth
      })

      const lineHeight = props.fontSize * 1.5
      height = contents.length * lineHeight + props.fontSize
      width = maxTextWidth
    } else {
      width = defaultWidth
      height = defaultHeight
    }
  }

  return {
    width,
    height,
  }
}

// 创建菱形布局的水印图案
const createDiamondPattern = async (
  watermarkPattern: string,
  unitWidth: number,
  unitHeight: number,
): Promise<string> => {
  try {
    const img = await loadImage(watermarkPattern)

    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    if (!ctx) {
      return watermarkPattern
    }

    const patternWidth = (unitWidth + props.gap[0]) * 2
    const patternHeight = (unitHeight + props.gap[1]) * 2

    canvas.width = patternWidth
    canvas.height = patternHeight

    // 绘制左上角水印单元
    ctx.drawImage(img, 0, 0, unitWidth, unitHeight)

    // 绘制右下角水印单元(菱形错位布局)
    const offsetX = unitWidth + props.gap[0]
    const offsetY = unitHeight + props.gap[1]
    ctx.drawImage(img, offsetX, offsetY, unitWidth, unitHeight)

    return canvas.toDataURL('image/png')
  } catch (error) {
    console.error('Failed to create diamond pattern:', error)
    return watermarkPattern
  }
}

// 生成水印图案
const generateWatermarkPattern = async (): Promise<{
  dataURL: string
  width: number
  height: number
} | null> => {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')

  if (!ctx) return null

  const { width, height } = calculateWatermarkSize(ctx)

  canvas.width = width
  canvas.height = height

  // 保存原始状态
  ctx.save()

  // 应用旋转变换
  ctx.translate(width / 2, height / 2)
  ctx.rotate((Math.PI / 180) * props.rotate)
  ctx.translate(-width / 2, -height / 2)

  const getImage = async () => {
    try {
      const img = await loadImage(props.image)
      const imgWidth = img.width * props.imageZoom
      const imgHeight = img.height * props.imageZoom

      const x = (width - imgWidth) / 2
      const y = (height - imgHeight) / 2

      ctx.globalAlpha = props.imageOpacity
      ctx.drawImage(img, x, y, imgWidth, imgHeight)
      ctx.globalAlpha = 1.0
      console.log('getImage', { ctx })
    } catch (error) {
      console.error('Failed to load watermark image:', error)
    }
  }

  const getContent = async () => {
    const contents = Array.isArray(props.content)
      ? props.content
      : [props.content]

    if (!contents.length || (contents.length === 1 && !contents[0])) {
      return
    }
    // 重置 globalAlpha 确保文字透明度正确
    ctx.globalAlpha = 1.0

    ctx.font = `${props.fontWeight} ${props.fontSize}px ${props.fontFamily}`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'

    const rgbaColor = hexToRgba(props.fontColor, props.textAlpha)

    ctx.fillStyle = rgbaColor

    const lineHeight = props.fontSize * 1.5
    const startY = (height - (contents.length - 1) * lineHeight) / 2

    contents.forEach((text, index) => {
      if (text) {
        const y = startY + index * lineHeight
        ctx.fillText(text, width / 2, y)
      }
    })
  }

  // 根据 layer 控制绘制顺序
  // layer=true: 图片在下层,文字在上层
  // layer=false: 文字在下层,图片在上层
  if (props.layer) {
    // 先绘制图片(下层)
    if (props.image) {
      await getImage()
    }
    // 再绘制文字(上层)
    if (props.content) {
      await getContent()
    }
  } else {
    // 先绘制文字(下层)
    if (props.content) {
      await getContent()
    }
    // 再绘制图片(上层)
    if (props.image) {
      await getImage()
    }
  }

  // 恢复状态
  ctx.restore()

  const dataURL = canvas.toDataURL('image/png')

  return {
    dataURL,
    width,
    height,
  }
}

// 加载图片
const loadImage = (src: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.src = src

    img.onload = () => resolve(img)
    img.onerror = reject
  })
}

// 十六进制颜色转RGBA
const hexToRgba = (hex: string, alpha: number): string => {
  if (hex.length === 4) {
    const r = parseInt(hex[1] + hex[1], 16)
    const g = parseInt(hex[2] + hex[2], 16)
    const b = parseInt(hex[3] + hex[3], 16)
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  }

  if (hex.length === 7) {
    const r = parseInt(hex.slice(1, 3), 16)
    const g = parseInt(hex.slice(3, 5), 16)
    const b = parseInt(hex.slice(5, 7), 16)
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  }

  return hex
}

// 设置防篡改监控
const setupMutationObserver = () => {
  if (!containerRef.value || !watermarkRef.value) return

  let originalBackgroundImage = ''
  let originalBackgroundRepeat = ''
  let originalBackgroundSize = ''
  let originalBackgroundPosition = ''

  const originalStyles = {
    position: watermarkRef.value.style.position,
    top: watermarkRef.value.style.top,
    left: watermarkRef.value.style.left,
    width: watermarkRef.value.style.width,
    height: watermarkRef.value.style.height,
    pointerEvents: watermarkRef.value.style.pointerEvents,
    zIndex: watermarkRef.value.style.zIndex,
    overflow: watermarkRef.value.style.overflow,
    opacity: watermarkRef.value.style.opacity,
    visibility: watermarkRef.value.style.visibility,
    display: watermarkRef.value.style.display,
  }

  observer.value = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.removedNodes.length > 0) {
        const removedNodes = Array.from(mutation.removedNodes)
        for (const node of removedNodes) {
          if (node === watermarkRef.value) {
            destroyWatermark(true) // 被篡改时立即销毁
            createWatermark()
            return
          }
        }
      }

      if (
        mutation.type === 'attributes' &&
        mutation.target === watermarkRef.value
      ) {
        if (watermarkRef.value) {
          Object.entries(originalStyles).forEach(([key, value]) => {
            if (watermarkRef.value) watermarkRef.value.style[key] = value
          })

          watermarkRef.value.style.backgroundImage = originalBackgroundImage
          watermarkRef.value.style.backgroundRepeat = originalBackgroundRepeat
          watermarkRef.value.style.backgroundSize = originalBackgroundSize
          watermarkRef.value.style.backgroundPosition =
            originalBackgroundPosition
        }
      }
    }
  })

  nextTick(() => {
    if (watermarkRef.value) {
      originalBackgroundImage = watermarkRef.value.style.backgroundImage
      originalBackgroundRepeat = watermarkRef.value.style.backgroundRepeat
      originalBackgroundSize = watermarkRef.value.style.backgroundSize
      originalBackgroundPosition = watermarkRef.value.style.backgroundPosition
    }
  })

  observer.value.observe(watermarkRef.value, {
    attributes: true,
    attributeFilter: ['style', 'class'],
  })

  observer.value.observe(containerRef.value, {
    childList: true,
    subtree: true,
  })

  const healthCheckInterval = setInterval(() => {
    if (!containerRef.value || !watermarkRef.value) return

    if (!containerRef.value.contains(watermarkRef.value)) {
      destroyWatermark(true) // 被移除时立即销毁
      createWatermark()
      return
    }

    const criticalStyles = [
      'position',
      'top',
      'left',
      'width',
      'height',
      'pointerEvents',
      'zIndex',
      'overflow',
      'backgroundImage',
      'backgroundRepeat',
      'backgroundSize',
      'backgroundPosition',
      'opacity',
      'visibility',
      'display',
    ]

    const computedStyle = window.getComputedStyle(watermarkRef.value)
    const isTampered = criticalStyles.some((style) => {
      const currentValue = computedStyle.getPropertyValue(style)
      const originalValue = originalStyles[style]
      return currentValue !== originalValue
    })

    if (isTampered) {
      Object.entries(originalStyles).forEach(([key, value]) => {
        if (watermarkRef.value) watermarkRef.value.style[key] = value
      })

      watermarkRef.value.style.backgroundImage = originalBackgroundImage
      watermarkRef.value.style.backgroundRepeat = originalBackgroundRepeat
      watermarkRef.value.style.backgroundSize = originalBackgroundSize
      watermarkRef.value.style.backgroundPosition = originalBackgroundPosition
    }
  }, 1000)

  cleanupFunctions.push(() => {
    clearInterval(healthCheckInterval)
    if (observer.value) {
      observer.value.disconnect()
      observer.value = null
    }
  })
}

// 设置响应式调整
const setupResponsive = () => {
  if (!containerRef.value || !props.responsive) return

  resizeObserver.value = new ResizeObserver(() => {
    createWatermark()
  })

  resizeObserver.value.observe(containerRef.value)
}

// 销毁水印（优化版：不立即移除，而是淡出）
const destroyWatermark = (immediate = false) => {
  if (watermarkRef.value && !immediate) {
    // 淡出动画
    watermarkRef.value.style.opacity = '0'

    setTimeout(() => {
      removeWatermarkDOM()
    }, 200)
  } else {
    removeWatermarkDOM()
  }
}

// 实际移除 DOM 的函数
const removeWatermarkDOM = () => {
  if (containerRef.value) {
    const watermarks = containerRef.value.querySelectorAll(
      `.${bem.e('content')}`,
    )
    watermarks.forEach((watermark) => {
      if (watermark.parentNode === containerRef.value) {
        containerRef.value?.removeChild(watermark)
      }
    })
  }

  watermarkRef.value = null

  if (observer.value) {
    observer.value.disconnect()
    observer.value = null
  }

  if (resizeObserver.value) {
    resizeObserver.value.disconnect()
    resizeObserver.value = null
  }

  cleanupFunctions.forEach((cleanup) => cleanup())
  cleanupFunctions.length = 0
}

// 创建水印元素（优化版：瞬间切换，无残影）
const createWatermark = async () => {
  if (!containerRef.value || isUpdating) return

  isUpdating = true

  try {
    // 创建新水印元素
    const watermarkDiv = document.createElement('div')
    watermarkDiv.className = bem.e('content')

    watermarkDiv.style.position = 'absolute'
    watermarkDiv.style.top = '0'
    watermarkDiv.style.left = '0'
    watermarkDiv.style.width = '100%'
    watermarkDiv.style.height = '100%'
    watermarkDiv.style.pointerEvents = 'none'
    watermarkDiv.style.zIndex = props.zIndex.toString()
    watermarkDiv.style.opacity = '1'

    // 先生成水印图案（包含异步图片加载）
    const watermarkPattern = await generateWatermarkPattern()

    if (watermarkPattern) {
      if (props.single) {
        watermarkDiv.style.backgroundImage = `url(${watermarkPattern.dataURL})`
        watermarkDiv.style.backgroundRepeat = 'no-repeat'

        const scaledWidth = watermarkPattern.width * props.singleScale
        const scaledHeight = watermarkPattern.height * props.singleScale

        watermarkDiv.style.backgroundSize = `${scaledWidth}px ${scaledHeight}px`
        watermarkDiv.style.backgroundPosition = positionMap[props.position]
      } else {
        const diamondPattern = await createDiamondPattern(
          watermarkPattern.dataURL,
          watermarkPattern.width,
          watermarkPattern.height,
        )
        watermarkDiv.style.backgroundImage = `url(${diamondPattern})`
        watermarkDiv.style.backgroundRepeat = 'repeat'
        watermarkDiv.style.backgroundPosition = `${props.offset[0]}px ${props.offset[1]}px`
      }
    }

    // 新水印准备好后，再移除旧水印（关键：避免闪动）
    const oldWatermark = watermarkRef.value
    if (oldWatermark && oldWatermark.parentNode) {
      oldWatermark.parentNode.removeChild(oldWatermark)
    }

    // 添加新水印
    if (containerRef.value) {
      containerRef.value.appendChild(watermarkDiv)
    }

    watermarkRef.value = watermarkDiv

    // 设置监控
    if (props.monitor) {
      setupMutationObserver()
    }

    isUpdating = false
  } catch (error) {
    console.error('Failed to create watermark:', error)
    isUpdating = false
  }
}

// 防抖更新水印
const debouncedUpdate = () => {
  // 清除之前的定时器
  if (updateTimer) {
    clearTimeout(updateTimer)
  }

  // 设置新的定时器
  updateTimer = setTimeout(() => {
    nextTick(() => {
      createWatermark()
    })
  }, 150) // 100ms 防抖延迟
}

// 监听属性变化
watch(
  () => [
    props.content,
    props.image,
    props.width,
    props.height,
    props.rotate,
    props.zIndex,
    props.gap,
    props.offset,
    props.fontSize,
    props.fontColor,
    props.textAlpha,
    props.fontFamily,
    props.fontWeight,
    props.imageOpacity,
    props.imageZoom,
    props.single,
    props.position,
    props.singleScale,
    props.layer,
  ],
  () => {
    debouncedUpdate()
  },
  {
    deep: true,
  },
)

onMounted(() => {
  nextTick(createWatermark)
  setupResponsive()
})

onUnmounted(() => {
  // 清理防抖定时器
  if (updateTimer) {
    clearTimeout(updateTimer)
  }
  destroyWatermark(true) // 卸载时立即销毁
})
defineExpose({ containerRef })
</script>
